import { ref } from 'vue'
import { apiClient } from '@/api/client'

/**
 * Accesso al widget «Da fare»: le cose che il sistema aspetta dall'utente e i promemoria
 * personali. Le chiamate stanno qui e non nel componente (regola frontend-web/CLAUDE.md #7).
 *
 * Il proprietario dei promemoria non è mai un parametro: il backend lo prende dal token.
 */

export interface VoceDaFare {
  tipo: string
  etichetta: string
  /** Data civile aaaa-MM-gg (o null). Si tratta come stringa: mai `new Date('aaaa-MM-gg')`. */
  data: string | null
  url: string
  isLegacy: boolean
}

export interface Promemoria {
  id: number
  testo: string
  /** Data civile aaaa-MM-gg, null per le note senza scadenza. */
  scadenza: string | null
  /** Ora civile facoltativa HH:mm (o HH:mm:ss dal backend), presente solo con la data. */
  scadenzaOra: string | null
  fatto: boolean
  fattoIl: string | null
}

export interface DaFare {
  tiAspettano: VoceDaFare[]
  promemoria: Promemoria[]
  fattiTotali: number
}

const BASE = '/api/v2/da-fare'

export function useDaFare() {
  const loading = ref(false)

  async function carica(): Promise<DaFare> {
    loading.value = true
    try {
      const res = await apiClient.get<DaFare>(BASE)
      return {
        tiAspettano: res.data?.tiAspettano ?? [],
        promemoria: res.data?.promemoria ?? [],
        fattiTotali: res.data?.fattiTotali ?? 0,
      }
    } finally {
      loading.value = false
    }
  }

  async function elenco(includiFatti: boolean): Promise<Promemoria[]> {
    const res = await apiClient.get<Promemoria[]>(`${BASE}/promemoria`, { params: { includiFatti } })
    return res.data ?? []
  }

  async function crea(testo: string, scadenza: string | null, scadenzaOra: string | null): Promise<Promemoria> {
    const res = await apiClient.post<Promemoria>(`${BASE}/promemoria`, { testo, scadenza, scadenzaOra })
    return res.data
  }

  /** Cambia testo e/o scadenza. Solo i propri: un id altrui risponde 404. */
  async function modifica(id: number, testo: string, scadenza: string | null, scadenzaOra: string | null): Promise<Promemoria> {
    const res = await apiClient.put<Promemoria>(`${BASE}/promemoria/${id}`, { testo, scadenza, scadenzaOra })
    return res.data
  }

  async function segna(id: number, fatto: boolean): Promise<Promemoria> {
    const res = await apiClient.patch<Promemoria>(`${BASE}/promemoria/${id}/fatto`, null, {
      params: { fatto },
    })
    return res.data
  }

  async function elimina(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/promemoria/${id}`)
  }

  return { loading, carica, elenco, crea, modifica, segna, elimina }
}
